// One `EngineRequest` over HTTP: the same request the desktop's main process sent its engine over
// stdin, posted to the job's channel. A write of one document carries the review version it is
// based on, and a 409 from the API's fence becomes a ReviewConflictError the screen listens for
// (review-versions.ts, ADR-0009). The route loader warms the first answers (engine-warmup.ts).
import type { EngineCall } from './desktop-commands';
import { takeWarmAnswer, warmEngine, warmKey, type WarmChannel } from './engine-warmup';
import { readReviewAnswer, withReviewVersion } from './review-versions';
import { readExportAnswer, type ExportOutcome } from './exported-once';
import type { ReviewLease } from './review-leases';
import { customerWords } from './engine-words';
import { isJobChannel } from './job-channel';
import { EngineUnreachableError } from './engine-unreachable';
import { EngineRefusedError } from './engine-refused';
import { readOnlyView } from './read-only';
import { engineDown, isEngineRestarting, queueForEngine } from './engine-updating';
import { answerLimit, EngineAnswerTimeoutError, isTimeout } from './engine-answer-limit';
import { notJustNow, problemWords } from './problem-words';
import { isPagesHeld, PagesHeldError } from './pages-held';

/** What a warm-up says when the engine is already known to be restarting (it does not ask). */
const UPDATING = 'Paige is updating, back in a moment.';

/**
 * The host's `EngineResponse` becomes the result or a thrown Error. While the engine host restarts
 * (a release) the call waits on the one shared probe and asks once it is answered, the workbench
 * saying so (engine-updating.ts); a call made while the probe runs does not ask at all until then.
 * A warm-up passes `waitOutRestart: false` so the route opens and the App's own calls do the waiting.
 */
export async function fetchEngine<T>(
  channel: WarmChannel,
  command: string,
  args: Record<string, unknown> = {},
  waitOutRestart = true,
): Promise<T> {
  if (engineDown()) {
    if (!waitOutRestart) throw new Error(UPDATING);
    return queued<T>(channel, command, args);
  }
  const first = await postEngine(channel, command, args);
  if (!waitOutRestart || !isEngineRestarting(first.response.status, first.body))
    return engineAnswer<T>(command, args, first);
  return queued<T>(channel, command, args);
}

function queued<T>(channel: WarmChannel, command: string, args: Record<string, unknown>) {
  return queueForEngine<T>(
    warmKey(channel, command, args),
    async () => {
      const probe = await postEngine(channel, 'health', {});
      return !isEngineRestarting(probe.response.status, probe.body);
    },
    async () => {
      const reply = await postEngine(channel, command, args);
      if (isEngineRestarting(reply.response.status, reply.body)) return { restarting: true };
      return { restarting: false, answer: () => engineAnswer<T>(command, args, reply) };
    },
  );
}

interface EngineReply {
  response: Response;
  body: EngineBody;
}

interface EngineBody {
  ok: boolean;
  result?: unknown;
  error?: { code: string; message: string };
  /** A refusal of Paige's own (a problem document), such as a read-only view's write. */
  detail?: string;
  title?: string;
  reviewVersion?: number;
  lease?: ReviewLease;
  export?: Partial<ExportOutcome>;
}

async function postEngine(
  channel: WarmChannel,
  command: string,
  args: Record<string, unknown>,
): Promise<EngineReply> {
  // Never an answer awaited for ever (engine-answer-limit.ts): past its limit the call fails as unreachable.
  const signal = AbortSignal.timeout(answerLimit(command));
  try {
    const response = await fetch(`${channel.engineOrigin}/engine`, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(channel.tenant ? { 'Paige-Tenant': channel.tenant } : {}),
      },
      credentials: 'include',
      signal,
      body: JSON.stringify({
        id: crypto.randomUUID(),
        command,
        ...withReviewVersion(command, args),
      }),
    });
    return { response, body: (await readBody(response)) as EngineBody };
  } catch (reason) {
    if (isTimeout(reason)) throw new EngineAnswerTimeoutError(command);
    throw reason;
  }
}

function engineAnswer<T>(
  command: string,
  args: Record<string, unknown>,
  { response, body }: EngineReply,
): T {
  readReviewAnswer(command, args, response.status, body);
  if (response.ok && body.ok) readExportAnswer(body);
  if (!response.ok || !body.ok) {
    // The credit hold keeps the capture to read later (pages-held.ts): it waits, it was not interrupted.
    if (body.detail && isPagesHeld(response.status, body))
      throw new PagesHeldError(problemWords(response.status, body));
    if (body.detail) throw new Error(problemWords(response.status, body));
    throw new EngineRefusedError(
      body.error?.message ? customerWords(body.error.message) : notJustNow,
    );
  }
  return body.result as T;
}

/** The engine's JSON, or EngineUnreachableError when something else answered (a proxy's error page). */
async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new EngineUnreachableError(response.status);
  }
}

/** The adapter's call: a warm answer when the route loader already asked, else the fetch. */
export function engineCall(engineOrigin: string, tenant?: string): EngineCall {
  const channel: WarmChannel = { engineOrigin, tenant };
  const tenantChannel = !isJobChannel(engineOrigin);
  return <T>(command: string, args: Record<string, unknown> = {}) => {
    // The tenant's channel (jobs home, New job) answers only what names no job; the API refuses
    // the rest with a 403. The App still polls its fallback job's overview there, so the refusal
    // is given here, the same as the API's, without a request every few seconds.
    if (tenantChannel && typeof args.jobId === 'string' && args.jobId !== '')
      return Promise.reject(new Error(`${command} is answered on its job's own page`));
    // A read-only view opens documents without telling the engine (the API would refuse the note).
    if (command === 'note-open-document' && readOnlyView()) return Promise.resolve(true as T);
    return takeWarmAnswer<T>(channel, command, args) ?? fetchEngine<T>(channel, command, args);
  };
}

/**
 * What the App asks the engine for the moment it opens a job: the job list, then the job's latest
 * batch overview. Paige's review route loader awaits this so the dashboard stays until the engine
 * has answered and the workbench can open in one step; a failure here is the App's to show.
 */
export async function warmWorkbench(
  engineOrigin: string,
  tenant: string | undefined,
  jobId: string,
): Promise<void> {
  const channel: WarmChannel = { engineOrigin, tenant };
  const jobs = warmEngine(channel, 'list-jobs', {}, () =>
    fetchEngine(channel, 'list-jobs', {}, false),
  );
  const overview = warmEngine(channel, 'latest-batch-overview', { jobId }, () =>
    fetchEngine(channel, 'latest-batch-overview', { jobId }, false),
  );
  await Promise.allSettled([jobs, overview]);
}
